/**
 * CapabilityPage — single, data-driven detail page for each landing pillar.
 *
 * Renders at `/capabilities/:slug` where slug ∈ {ai-vision, pricing-smart,
 * gcode-cam, machines-catalog, workflow, output-integrations}.
 *
 * Each pillar's copy lives under `landing.detail.<key>` in the i18n bundles.
 * SEO meta (title, description, og:*) is updated on mount per pillar so search
 * engines and social shares get unique snippets per page.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, ArrowLeft, Check, Star, ChevronDown, HelpCircle,
  Eye, Calculator, FileCode2, Database, Workflow, Send,
} from "lucide-react";
import BrandIcon from "../components/app/BrandIcon";
import LanguageSwitcher from "../components/app/LanguageSwitcher";
import { getLangPrefix, useLangPrefix } from "../lib/langRoutes";
import { canonicalizeCapSlug, capSlugFor, capSegmentFor, usePublicPaths } from "../lib/localizedSlugs";

/* ---------- Static slug → meta config (icons, accent, related pillars) --------- */
const CAPABILITY_CONFIG = {
  "ai-vision":           { key: "ai",       Icon: Eye,        accent: "blue",    related: ["gcode-cam", "machines-catalog"] },
  "pricing-smart":       { key: "pricing",  Icon: Calculator, accent: "cyan",    related: ["ai-vision", "output-integrations"] },
  "gcode-cam":           { key: "gcode",    Icon: FileCode2,  accent: "violet",  related: ["ai-vision", "machines-catalog"] },
  "machines-catalog":    { key: "machines", Icon: Database,   accent: "emerald", related: ["gcode-cam", "workflow"] },
  "workflow":            { key: "workflow", Icon: Workflow,   accent: "amber",   related: ["machines-catalog", "pricing-smart"] },
  "output-integrations": { key: "output",   Icon: Send,       accent: "rose",    related: ["workflow", "pricing-smart"] },
};

const ACCENT_STYLES = {
  blue:    { ring: "ring-blue-500/20",    text: "text-blue-300",    border: "border-blue-500/40",    bg: "bg-blue-500/10",    glow: "shadow-[0_0_40px_rgba(59,130,246,0.18)]"  },
  cyan:    { ring: "ring-cyan-500/20",    text: "text-cyan-300",    border: "border-cyan-500/40",    bg: "bg-cyan-500/10",    glow: "shadow-[0_0_40px_rgba(34,211,238,0.18)]"  },
  violet:  { ring: "ring-violet-500/20",  text: "text-violet-300",  border: "border-violet-500/40",  bg: "bg-violet-500/10",  glow: "shadow-[0_0_40px_rgba(167,139,250,0.18)]" },
  emerald: { ring: "ring-emerald-500/20", text: "text-emerald-300", border: "border-emerald-500/40", bg: "bg-emerald-500/10", glow: "shadow-[0_0_40px_rgba(52,211,153,0.18)]"  },
  amber:   { ring: "ring-amber-500/20",   text: "text-amber-300",   border: "border-amber-500/40",   bg: "bg-amber-500/10",   glow: "shadow-[0_0_40px_rgba(251,191,36,0.18)]"  },
  rose:    { ring: "ring-rose-500/20",    text: "text-rose-300",    border: "border-rose-500/40",    bg: "bg-rose-500/10",    glow: "shadow-[0_0_40px_rgba(244,114,182,0.18)]" },
};

/* ---------- Per-pillar SEO meta override (replaces useDocumentMeta default) -- */
const SITE_ORIGIN = "https://machinepilot.eu";
const HREFLANGS = ["it", "en", "de", "fr", "es", "pl"];

function setLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function useCapabilityMeta(slug, metaTitle, metaDesc, faqList) {
  useEffect(() => {
    if (metaTitle) document.title = metaTitle;
    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const parts = selector.match(/\[([^=]+)="?([^"\]]+)"?\]/);
        if (parts) el.setAttribute(parts[1], parts[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    if (metaDesc) {
      setMeta('meta[name="description"]', "content", metaDesc);
      setMeta('meta[property="og:description"]', "content", metaDesc);
      setMeta('meta[name="twitter:description"]', "content", metaDesc);
    }
    if (metaTitle) {
      setMeta('meta[property="og:title"]', "content", metaTitle);
      setMeta('meta[name="twitter:title"]', "content", metaTitle);
    }
    // Per-pillar OG image + URL + canonical + hreflang alternates.
    // Italiano canonico alla root: /funzionalita/{slug-it}; altre lingue
    // /{lang}/{segmento}/{slug}. x-default = versione italiana.
    if (slug) {
      const prefix = getLangPrefix(window.location.pathname);
      const lang = prefix.slice(1) || "it";
      const hrefFor = (lng) =>
        lng === "it"
          ? `${SITE_ORIGIN}/${capSegmentFor("it")}/${capSlugFor(slug, "it")}`
          : `${SITE_ORIGIN}/${lng}/${capSegmentFor(lng)}/${capSlugFor(slug, lng)}`;
      const pageUrl = hrefFor(lang);
      const ogImage = `${SITE_ORIGIN}/og-${slug}.png`;
      setMeta('meta[property="og:url"]', "content", pageUrl);
      setMeta('meta[property="og:image"]', "content", ogImage);
      setMeta('meta[name="twitter:image"]', "content", ogImage);
      setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
      setLink("canonical", pageUrl);
      HREFLANGS.forEach((lng) => setLink("alternate", hrefFor(lng), lng));
      setLink("alternate", hrefFor("it"), "x-default");
    }
    // SoftwareApplication structured data
    let jsonLd = document.head.querySelector('script[data-id="capability-jsonld"]');
    if (!jsonLd) {
      jsonLd = document.createElement("script");
      jsonLd.type = "application/ld+json";
      jsonLd.setAttribute("data-id", "capability-jsonld");
      document.head.appendChild(jsonLd);
    }
    jsonLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "MachinePilot",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      description: metaDesc,
    });

    // FAQPage structured data — drives Google rich snippets (accordion under
    // the organic search result). Only inject if we have ≥ 2 Q/A pairs.
    let faqLd = document.head.querySelector('script[data-id="capability-faq-jsonld"]');
    if (Array.isArray(faqList) && faqList.length >= 2) {
      if (!faqLd) {
        faqLd = document.createElement("script");
        faqLd.type = "application/ld+json";
        faqLd.setAttribute("data-id", "capability-faq-jsonld");
        document.head.appendChild(faqLd);
      }
      faqLd.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqList.map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
      });
    } else if (faqLd) {
      // No FAQ for this slug → remove stale schema from previous pillar
      faqLd.remove();
    }
  }, [slug, metaTitle, metaDesc, faqList]);
}

export default function CapabilityPage() {
  const { slug: rawSlug } = useParams();
  const slug = canonicalizeCapSlug(rawSlug) || rawSlug;
  const { t } = useTranslation();
  const lp = useLangPrefix();
  const pp = usePublicPaths();
  const { pathname } = useLocation();
  const lang = lp.slice(1);
  const config = CAPABILITY_CONFIG[slug];

  // Always-call hooks: pass empty strings when config is missing so we
  // don't violate React's rules-of-hooks before the Navigate fallback below.
  const metaTitle = t(`landing.detail.${config?.key || ""}.metaTitle`, "");
  const metaDesc = t(`landing.detail.${config?.key || ""}.metaDesc`, "");

  // Reset scroll position so deep-linked users land at the hero, not mid-page.
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [slug]);

  const bullets = useMemo(() => {
    const raw = t(`landing.detail.${config?.key || ""}.bullets`, { returnObjects: true });
    return Array.isArray(raw) ? raw : [];
  }, [t, config]);

  const faqList = useMemo(() => {
    const raw = t(`landing.detail.${config?.key || ""}.faq`, { returnObjects: true });
    return Array.isArray(raw) ? raw.filter((x) => x && x.q && x.a) : [];
  }, [t, config]);

  useCapabilityMeta(config ? slug : "", metaTitle, metaDesc, faqList);

  if (!config) return <Navigate to="/" replace />;

  // Enforce the language-local segment + slug (redirect legacy/wrong-language URLs)
  if (lang) {
    const seg = pathname.split("/")[2];
    const expSeg = capSegmentFor(lang);
    const expSlug = capSlugFor(slug, lang);
    if (seg !== expSeg || rawSlug !== expSlug) {
      return <Navigate to={`/${lang}/${expSeg}/${expSlug}`} replace />;
    }
  } else {
    // Canonico italiano alla root: /funzionalita/{slug-it}
    const seg = pathname.split("/")[1];
    const expSeg = capSegmentFor("it");
    const expSlug = capSlugFor(slug, "it");
    if (seg !== expSeg || rawSlug !== expSlug) {
      return <Navigate to={`/${expSeg}/${expSlug}`} replace />;
    }
  }

  const { Icon, accent, related } = config;
  const cls = ACCENT_STYLES[accent];
  const hero = t(`landing.detail.${config.key}.hero`);
  const lede = t(`landing.detail.${config.key}.lede`);
  const tagline = t("landing.capabilityCommon.tagLine");
  const testimonial = t(`landing.detail.${config.key}.testimonial`, { returnObjects: true });

  return (
    <div className="theme-swiss min-h-screen bg-slate-950 text-slate-100" data-testid={`capability-page-${slug}`}>
      {/* Compact header with back nav */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-slate-950/80 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to={`${lp}/`} className="flex items-center gap-2 group">
            <BrandIcon size={32} className="drop-shadow-[0_0_14px_rgba(232,89,12,0.45)]" />
            <div className="font-display font-bold text-slate-100 group-hover:text-blue-300 transition-colors">{t("app.name")}</div>
          </Link>
          <Link to={`${lp}/#capabilities`} className="hidden md:flex items-center gap-2 text-xs text-slate-400 hover:text-cyan-300 font-mono uppercase tracking-wider" data-testid="capability-back-home">
            <ArrowLeft size={12}/> {t("landing.capabilityCommon.backToHome")}
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact/>
            <Link to="/register" className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2" data-testid="capability-cta-top">
              {t("landing.cta")} <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className={`relative overflow-hidden border-b border-slate-800/60 ring-1 ${cls.ring}`}>
        <div className="absolute inset-0 bg-mc-grid opacity-[0.25]"/>
        <div className={`absolute inset-0 ${cls.bg} mix-blend-screen opacity-30`}/>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border ${cls.border} ${cls.bg} font-mono text-[10px] tracking-[0.22em] uppercase ${cls.text}`}>
              <Icon size={11} aria-hidden="true"/>
              {t(`landing.capabilities.pillars.${config.key}.tag`)} · {tagline}
            </div>
            <h1 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-white" data-testid="capability-hero-title">
              {hero}
            </h1>
            <p className="mt-7 text-slate-300 text-base lg:text-lg leading-relaxed max-w-2xl">{lede}</p>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <Link to="/register" className="btn-primary px-6 py-3 rounded-sm font-mono text-sm tracking-wider uppercase flex items-center gap-2" data-testid="capability-cta-hero">
                {t("landing.capabilityCommon.ctaPrimary")} <ArrowRight size={14}/>
              </Link>
              <a href="mailto:support@machinepilot.eu" className="px-6 py-3 rounded-sm border border-slate-700 text-slate-300 hover:border-cyan-500/50 hover:text-cyan-400 font-mono text-sm tracking-wider uppercase flex items-center gap-2 transition-colors">
                {t("landing.capabilityCommon.ctaSecondary")}
              </a>
            </div>
          </div>
          {/* Right column — graphic placeholder using Icon as visual anchor */}
          <div className="lg:col-span-5 flex items-center justify-center">
            <div className={`relative w-full max-w-md aspect-square border ${cls.border} ${cls.glow} bg-slate-950/80 corner-bracket overflow-hidden`}>
              <div className={`absolute inset-0 ${cls.bg} opacity-40`}/>
              <div className="absolute inset-0 bg-mc-grid opacity-[0.4] pointer-events-none"/>
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon size={120} className={`${cls.text} opacity-70`} strokeWidth={1.1} aria-hidden="true"/>
              </div>
              <div className="absolute top-3 left-3 font-mono text-[9px] tracking-[0.2em] text-slate-400 uppercase">
                {t("landing.capabilityCommon.previewTitle")}
              </div>
              <div className="absolute bottom-3 right-3 font-mono text-[9px] text-slate-500">{slug.toUpperCase()}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Bullets — expanded paragraphs */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="grid md:grid-cols-2 gap-5">
          {bullets.map((b, i) => (
            <div key={`bullet-${i}`} className={`tech-card p-6 ${cls.border} hover:${cls.glow} transition-all`} data-testid={`capability-bullet-${i}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-9 h-9 rounded-md ${cls.bg} flex items-center justify-center shrink-0 border ${cls.border}`}>
                  <Check size={16} className={cls.text}/>
                </div>
                <div className="font-display font-semibold text-slate-100 text-lg leading-tight">{b.title}</div>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial */}
      {testimonial && testimonial.body && (
        <section className="bg-slate-900/30 border-y border-slate-800">
          <div className="max-w-3xl mx-auto px-6 py-16 lg:py-20 text-center">
            <div className="flex items-center justify-center gap-1 mb-5">
              {[0,1,2,3,4].map((i) => <Star key={i} size={16} className="text-amber-400 fill-amber-400"/>)}
            </div>
            <p className="text-slate-100 text-xl lg:text-2xl leading-relaxed font-display italic" data-testid="capability-testimonial-body">
              &ldquo;{testimonial.body}&rdquo;
            </p>
            <div className="mt-6 font-display font-semibold text-slate-100">{testimonial.author}</div>
            <div className="text-sm text-slate-500 font-mono">{testimonial.role}</div>
          </div>
        </section>
      )}

      {/* FAQ — accordion + JSON-LD FAQPage schema for rich snippets */}
      {faqList.length > 0 && (
        <section className="bg-slate-950 border-y border-slate-800/60" data-testid="capability-faq">
          <div className="max-w-4xl mx-auto px-6 py-16 lg:py-20">
            <div className="flex items-center gap-3 mb-3">
              <HelpCircle size={20} className={cls.text} aria-hidden="true"/>
              <div className={`label-tech ${cls.text} text-[10px]`}>FAQ</div>
            </div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-white">
              {t("landing.capabilityCommon.faqTitle")}
            </h2>
            <p className="mt-3 text-slate-400 text-base leading-relaxed max-w-2xl">
              {t("landing.capabilityCommon.faqSub")}
            </p>
            <div className="mt-8 divide-y divide-slate-800 border border-slate-800 rounded-sm bg-slate-950/60">
              {faqList.map((item, i) => (
                <FaqItem
                  key={`faq-${slug}-${i}`}
                  question={item.q}
                  answer={item.a}
                  accent={cls}
                  testid={`capability-faq-item-${i}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related capabilities */}
      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="label-tech text-slate-500 mb-6">{t("landing.capabilityCommon.alsoSee")}</div>
        <div className="grid md:grid-cols-2 gap-4">
          {related.map((rslug) => {
            const rcfg = CAPABILITY_CONFIG[rslug];
            if (!rcfg) return null;
            const rIcon = rcfg.Icon;
            const rcls = ACCENT_STYLES[rcfg.accent];
            return (
              <Link
                key={rslug}
                to={pp.cap(rslug)}
                className={`tech-card p-5 flex items-center gap-4 group hover:${rcls.glow} ${rcls.border} hover:-translate-y-0.5 transition-all`}
                data-testid={`capability-related-${rslug}`}
              >
                <div className={`w-12 h-12 rounded-md ${rcls.bg} flex items-center justify-center shrink-0`}>
                  <rIcon size={20} className={rcls.text} aria-hidden="true"/>
                </div>
                <div className="flex-1">
                  <div className={`font-mono text-[9px] tracking-[0.2em] uppercase ${rcls.text} mb-1`}>
                    {t(`landing.capabilities.pillars.${rcfg.key}.tag`)}
                  </div>
                  <div className="font-display font-semibold text-slate-100">
                    {t(`landing.capabilities.pillars.${rcfg.key}.title`)}
                  </div>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-cyan-300 transition-colors"/>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden">
        <div className={`absolute inset-0 ${cls.bg} opacity-30`}/>
        <div className="absolute inset-0 bg-grid opacity-[0.08]"/>
        <div className="relative max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight text-white">
            {t("landing.capabilityCommon.ctaTitle")}
          </h2>
          <p className="mt-5 text-slate-300 text-lg max-w-2xl mx-auto">{t("landing.capabilityCommon.ctaSub")}</p>
          <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
            <Link to="/register" className="btn-primary px-7 py-3.5 rounded-md font-medium flex items-center gap-2 text-base shadow-[0_0_40px_rgba(59,130,246,0.4)]" data-testid="capability-cta-final">
              {t("landing.capabilityCommon.ctaPrimary")} <ArrowRight size={16}/>
            </Link>
            <Link to={`${lp}/`} className="px-6 py-3.5 rounded-md text-sm text-slate-200 border border-slate-700 hover:border-blue-500 transition-colors">
              {t("landing.capabilityCommon.backToHome")}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between flex-wrap gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <BrandIcon size={24} />
            {t("landing.footer")}
          </div>
          <LanguageSwitcher compact/>
        </div>
      </footer>
    </div>
  );
}

/* ---------- FAQ accordion item ---------- */
function FaqItem({ question, answer, accent, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-slate-900/40 focus:outline-none focus:bg-slate-900/40 transition-colors"
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
      >
        <span className="font-display text-base lg:text-lg text-slate-100 font-medium leading-snug">
          {question}
        </span>
        <ChevronDown
          size={18}
          className={`${accent.text} shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="px-5 pb-5 -mt-1 text-sm text-slate-300 leading-relaxed" data-testid={`${testid}-body`}>
          {answer}
        </div>
      )}
    </div>
  );
}

